import type {
  Control,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
} from "react-hook-form";

import { RequiredMark } from "@/components/settings/nutrition-shared";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { DiseaseFormValues } from "./diseaseFormSchema";

interface DiseaseFormFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  dirtyFields: UseFormStateReturn<TFieldValues>["dirtyFields"];
}

const labelClass = (dirty: boolean) =>
  cn("z-label mb-0 w-[170px] shrink-0", dirty && "font-semibold text-warning");

const inputClass = (dirty: boolean) =>
  cn("flex-1", dirty && "border-2 border-warning");

const isDirtyField = (
  dirtyFields: DiseaseFormFieldsProps<DiseaseFormValues>["dirtyFields"],
  name: keyof DiseaseFormValues
) => Boolean(dirtyFields[name]);

const textFields: Array<{
  name: keyof DiseaseFormValues;
  label: string;
  required?: boolean;
  multiline?: boolean;
}> = [
  { name: "long_name", label: "Long Name", required: true },
  { name: "short_name", label: "Short Name", required: true },
  { name: "display_name", label: "Display Name" },
  { name: "icd10_code", label: "ICD-10 Code" },
  { name: "other_names", label: "Other Names", multiline: true },
];

const booleanFields: Array<{ name: keyof DiseaseFormValues; label: string }> = [
  { name: "widely_prevalent", label: "Widely Prevalent" },
  { name: "family_linked", label: "Family Linked" },
  { name: "is_featured", label: "Featured" },
];

export const DiseaseFormFields = ({
  control,
  dirtyFields,
}: DiseaseFormFieldsProps<DiseaseFormValues>) => (
  <div className="space-y-5">
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-2">
      {textFields.map((fieldConfig) => (
        <FormField
          key={fieldConfig.name}
          control={control}
          name={fieldConfig.name as FieldPath<DiseaseFormValues>}
          render={({ field }) => {
            const dirty = isDirtyField(dirtyFields, fieldConfig.name);
            return (
              <FormItem
                className={fieldConfig.multiline ? "lg:col-span-2" : ""}
              >
                <div
                  className={cn(
                    "flex gap-4",
                    fieldConfig.multiline ? "items-start" : "items-center"
                  )}
                >
                  <FormLabel
                    className={cn(
                      labelClass(dirty),
                      fieldConfig.multiline && "pt-3"
                    )}
                  >
                    {fieldConfig.label}
                    {fieldConfig.required ? <RequiredMark /> : null}
                  </FormLabel>
                  <FormControl>
                    {fieldConfig.multiline ? (
                      <Textarea
                        {...field}
                        value={String(field.value ?? "")}
                        className={cn(
                          inputClass(dirty),
                          "min-h-24 rounded-2xl"
                        )}
                      />
                    ) : (
                      <Input
                        {...field}
                        value={String(field.value ?? "")}
                        className={inputClass(dirty)}
                      />
                    )}
                  </FormControl>
                </div>
                <FormMessage className="ml-[186px]" />
              </FormItem>
            );
          }}
        />
      ))}
    </div>

    <div className="grid grid-cols-1 gap-x-8 gap-y-4 rounded-3xl border border-border p-5 lg:grid-cols-2">
      {booleanFields.map((fieldConfig) => (
        <FormField
          key={fieldConfig.name}
          control={control}
          name={fieldConfig.name as FieldPath<DiseaseFormValues>}
          render={({ field }) => {
            const dirty = isDirtyField(dirtyFields, fieldConfig.name);
            return (
              <FormItem>
                <div className="flex items-center gap-4">
                  <FormLabel className={labelClass(dirty)}>
                    {fieldConfig.label}
                  </FormLabel>
                  <FormControl>
                    <Switch
                      checked={Boolean(field.value)}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </div>
              </FormItem>
            );
          }}
        />
      ))}
    </div>
  </div>
);
